import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, Pencil, Save, Share2, Link2, Star, FolderOpen, Search, Bell, BellOff, UserPlus, UserMinus } from 'lucide-react';
import { apiClient } from '../apiClient';
import SelectMenu from '../components/SelectMenu';
import VisibilityIndicator from '../components/VisibilityIndicator';
import { richTextToPlainText } from '../utils/richText';
import Modal from '../components/Modal';
import ImageUploadButton from '../components/ImageUploadButton';
import AuthContext from '../context/AuthContext';
import { buildProfilePath, matchesProfileIdentifier } from '../utils/profileRoutes';

function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, setUser } = React.useContext(AuthContext);
  const [currentUser, setCurrentUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [projectSearch, setProjectSearch] = useState('');
  const [promptSearch, setPromptSearch] = useState('');
  const [projectSort, setProjectSort] = useState('updated_desc');
  const [promptSort, setPromptSort] = useState('updated_desc');
  const [copied, setCopied] = useState(false);
  const [followStatus, setFollowStatus] = useState({ following: false, notify: false });
  const [followLoading, setFollowLoading] = useState(false);
  const [followersData, setFollowersData] = useState({ count: 0, items: [], private: false });
  const [followingData, setFollowingData] = useState({ count: 0, items: [], private: false });
  const [followError, setFollowError] = useState('');
  const [followModal, setFollowModal] = useState(null);
  const [followSearch, setFollowSearch] = useState('');
  const [followVisibleCount, setFollowVisibleCount] = useState(12);
  const [uploadingMedia, setUploadingMedia] = useState({ avatar: false, banner: false });
  const [formState, setFormState] = useState({
    name: '',
    username: '',
    bio: '',
    links: ['', '', ''],
    avatar_url: '',
    banner_url: '',
    is_public: true
  });

  const isOwner = useMemo(() => {
    if (!currentUser || !profile) return false;
    return currentUser.user_id === profile.user_id;
  }, [currentUser, profile]);

  const shouldOpenProfileSetup = useMemo(() => {
    return new URLSearchParams(location.search).get('edit') === '1';
  }, [location.search]);

  useEffect(() => {
    if (authUser?.user_id) {
      setCurrentUser(authUser);
    }
  }, [authUser]);

  useEffect(() => {
    const loadCurrent = async () => {
      try {
        const data = await apiClient.get('/api/auth/me');
        setCurrentUser(data);
      } catch (err) {
        // ignore
      }
    };
    loadCurrent();
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError('');
      try {
        if (!userId) {
          const data = await apiClient.get('/api/users/profile');
          setProfile(data);
          setFormState({
            name: data.name || '',
            username: data.username || '',
            bio: data.bio || '',
            links: Array.isArray(data.links) ? [...data.links, '', '', ''].slice(0, 3) : ['', '', ''],
            avatar_url: data.avatar_url || '',
            banner_url: data.banner_url || '',
            is_public: data.is_public ?? true
          });
          return;
        }

        if (matchesProfileIdentifier(
          { user_id: currentUser?.user_id, username: currentUser?.username },
          userId
        )) {
          const data = await apiClient.get('/api/users/profile');
          setProfile(data);
          setFormState({
            name: data.name || '',
            username: data.username || '',
            bio: data.bio || '',
            links: Array.isArray(data.links) ? [...data.links, '', '', ''].slice(0, 3) : ['', '', ''],
            avatar_url: data.avatar_url || '',
            banner_url: data.banner_url || '',
            is_public: data.is_public ?? true
          });
        } else {
          const data = await apiClient.get(`/api/users/${userId}`);
          setProfile(data);
        }
      } catch (err) {
        setError(err.message || 'Unable to load profile.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [userId, currentUser?.user_id, currentUser?.username]);

  useEffect(() => {
    if (!profile?.user_id) return;
    const loadContent = async () => {
      try {
        if (isOwner) {
          const [projectList, promptList] = await Promise.all([
            apiClient.get('/api/projects'),
            apiClient.get('/api/prompts')
          ]);
          setProjects(projectList);
          setPrompts(promptList);
        } else {
          const [projectList, promptList] = await Promise.all([
            apiClient.get('/api/public/projects', { user_id: profile.user_id }),
            apiClient.get('/api/public/prompts', { user_id: profile.user_id })
          ]);
          setProjects(projectList);
          setPrompts(promptList);
        }
      } catch (err) {
        // ignore
      }
    };
    loadContent();
  }, [profile?.user_id, isOwner]);

  useEffect(() => {
    if (!profile?.user_id || !currentUser?.user_id) return;
    const loadFollowState = async () => {
      try {
        const [status, followers, following] = await Promise.all([
          apiClient.get(`/api/users/${profile.user_id}/follow-status`),
          apiClient.get(`/api/users/${profile.user_id}/followers`),
          apiClient.get(`/api/users/${profile.user_id}/following`)
        ]);
        setFollowStatus(status);
        setFollowersData(followers);
        setFollowingData(following);
      } catch (err) {
        // ignore
      }
    };
    loadFollowState();
  }, [profile?.user_id, currentUser?.user_id]);

  useEffect(() => {
    if (!profile?.user_id || !selectedProjectId) {
      setFolders([]);
      setSelectedFolderId(null);
      return;
    }
    const loadFolders = async () => {
      try {
        const data = isOwner
          ? await apiClient.get('/api/folders', { project_id: selectedProjectId })
          : await apiClient.get('/api/public/folders', { user_id: profile.user_id, project_id: selectedProjectId });
        setFolders(data);
      } catch (err) {
        // ignore
      }
    };
    loadFolders();
  }, [profile?.user_id, selectedProjectId, isOwner]);

  useEffect(() => {
    if (isOwner && shouldOpenProfileSetup) {
      setEditMode(true);
    }
  }, [isOwner, shouldOpenProfileSetup]);

  const handleFilePick = async (file, field) => {
    if (!file) return;
    const mediaKind = field === 'avatar_url' ? 'avatar' : 'banner';
    setUploadingMedia((prev) => ({ ...prev, [mediaKind]: true }));
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.request(`/api/users/profile/${mediaKind}`, {
        method: 'POST',
        body: formData
      });
      const data = response.data;
      setProfile((prev) => ({ ...prev, ...data }));
      setCurrentUser((prev) => (prev ? { ...prev, ...data } : { ...(authUser || {}), ...data }));
      setUser((prev) => (prev ? { ...prev, ...data } : { ...(authUser || {}), ...data }));
      setFormState((prev) => ({
        ...prev,
        avatar_url: data.avatar_url || prev.avatar_url,
        banner_url: data.banner_url || prev.banner_url
      }));
    } catch (err) {
      setError(err.message || `Unable to upload ${mediaKind}.`);
    } finally {
      setUploadingMedia((prev) => ({ ...prev, [mediaKind]: false }));
    }
  };

  const handleDeleteMedia = async (mediaKind) => {
    setUploadingMedia((prev) => ({ ...prev, [mediaKind]: true }));
    setError('');
    try {
      const response = await apiClient.delete(`/api/users/profile/${mediaKind}`);
      const data = response;
      setProfile((prev) => ({ ...prev, ...data }));
      setCurrentUser((prev) => (prev ? { ...prev, ...data } : { ...(authUser || {}), ...data }));
      setUser((prev) => (prev ? { ...prev, ...data } : { ...(authUser || {}), ...data }));
      setFormState((prev) => ({
        ...prev,
        avatar_url: mediaKind === 'avatar' ? '' : prev.avatar_url,
        banner_url: mediaKind === 'banner' ? '' : prev.banner_url
      }));
    } catch (err) {
      setError(err.message || `Unable to remove ${mediaKind}.`);
    } finally {
      setUploadingMedia((prev) => ({ ...prev, [mediaKind]: false }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: formState.name,
        username: formState.username,
        bio: formState.bio,
        links: formState.links.filter((link) => link.trim()).slice(0, 3),
        is_public: formState.is_public
      };
      const data = await apiClient.patch('/api/users/profile', payload);
      setProfile((prev) => ({ ...prev, ...data }));
      setCurrentUser((prev) => (prev ? { ...prev, ...data } : { ...(authUser || {}), ...data }));
      setUser((prev) => (prev ? { ...prev, ...data } : { ...(authUser || {}), ...data }));
      setEditMode(false);
    } catch (err) {
      setError(err.message || 'Unable to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyProfile = async () => {
    try {
      const link = `${window.location.origin}${buildProfilePath(profile)}`;
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setCopied(false);
    }
  };

  const refreshFollowCounts = async () => {
    try {
      const [followers, following] = await Promise.all([
        apiClient.get(`/api/users/${profile.user_id}/followers`),
        apiClient.get(`/api/users/${profile.user_id}/following`)
      ]);
      setFollowersData(followers);
      setFollowingData(following);
    } catch (err) {
      // ignore
    }
  };

  const handleFollowToggle = async () => {
    if (!profile?.user_id || followLoading) return;
    setFollowLoading(true);
    setFollowError('');
    try {
      if (followStatus.following) {
        await apiClient.delete(`/api/users/${profile.user_id}/follow`);
        setFollowStatus({ following: false, notify: false });
      } else {
        await apiClient.post(`/api/users/${profile.user_id}/follow`, {});
        setFollowStatus({ following: true, notify: false });
      }
      await refreshFollowCounts();
    } catch (err) {
      setFollowError(err.message || 'Unable to update follow status.');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleNotifyToggle = async () => {
    if (!followStatus.following || followLoading) return;
    setFollowLoading(true);
    setFollowError('');
    try {
      const next = !followStatus.notify;
      const data = await apiClient.post(`/api/users/${profile.user_id}/follow/notify`, { enabled: next });
      setFollowStatus((prev) => ({ ...prev, notify: data.notify }));
    } catch (err) {
      setFollowError(err.message || 'Unable to update notification preference.');
    } finally {
      setFollowLoading(false);
    }
  };

  const tabs = useMemo(() => {
    const base = [
      { key: 'overview', label: 'Overview' },
      { key: 'projects', label: 'Projects' },
      { key: 'prompts', label: 'Prompts' }
    ];
    if (isOwner) {
      base.push({ key: 'favorites', label: 'Favorites' });
      base.push({ key: 'saved', label: 'Saved' });
    }
    return base;
  }, [isOwner]);

  const projectFiltered = useMemo(() => {
    const filtered = projects.filter((project) =>
      project.name.toLowerCase().includes(projectSearch.toLowerCase())
    );
    const sorted = [...filtered];
    switch (projectSort) {
      case 'name_asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'name_desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'created_desc':
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'created_asc':
        return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'updated_asc':
        return sorted.sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at));
      case 'updated_desc':
      default:
        return sorted.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    }
  }, [projects, projectSearch, projectSort]);

  const promptFiltered = useMemo(() => {
    const filtered = prompts.filter((prompt) =>
      prompt.name.toLowerCase().includes(promptSearch.toLowerCase())
    );
    const sorted = [...filtered];
    switch (promptSort) {
      case 'name_asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'name_desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'created_desc':
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'created_asc':
        return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'rating_desc':
        return sorted.sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));
      case 'updated_asc':
        return sorted.sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at));
      case 'updated_desc':
      default:
        return sorted.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    }
  }, [prompts, promptSearch, promptSort]);

  if (loading) {
    return (
      <div className="min-h-screen profile-page">
        <div className="profile-hero card-glass profile-skeleton">
          <div className="skeleton-banner" />
          <div className="skeleton-row">
            <div className="skeleton-avatar" />
            <div className="skeleton-lines">
              <div className="skeleton-line wide" />
              <div className="skeleton-line" />
            </div>
          </div>
        </div>
        <div className="profile-stats">
          {[1, 2, 3].map((item) => (
            <div key={item} className="stat-pill card-glass skeleton-line" />
          ))}
        </div>
        <div className="card-glass skeleton-line" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen app-page-shell">
        <div className="card-glass max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-2">Profile unavailable</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button className="btn-primary" onClick={() => navigate('/profile')}>
            Back to Profile
          </button>
        </div>
      </div>
    );
  }

  const displayName = profile?.name || profile?.username || 'Rebel User';
  const handle = profile?.username ? `@${profile.username}` : '';
  const linkList = (profile?.links || []).filter(Boolean).slice(0, 3);
  const normalizeLink = (link) => (link.match(/^https?:\/\//i) ? link : `https://${link}`);
  const ratedPrompts = prompts.filter((prompt) => (prompt.rating_count || 0) > 0);
  const totalRatings = ratedPrompts.reduce((sum, prompt) => sum + (prompt.rating_count || 0), 0);
  const avgRating = totalRatings === 0
    ? 0
    : Number(
        (
          ratedPrompts.reduce(
            (sum, prompt) => sum + (prompt.average_rating || 0) * (prompt.rating_count || 0),
            0
          ) / totalRatings
        ).toFixed(1)
      );
  const followerCount = Number(followersData?.count ?? profile?.stats?.followers ?? 0);
  const followingCount = Number(followingData?.count ?? profile?.stats?.following ?? 0);
  const stats = [
    { label: 'Public Prompts', value: prompts.filter((p) => p.visibility === 'public').length },
    { label: 'Public Projects', value: projects.filter((p) => p.visibility === 'public').length },
    { label: `Avg Rating (${totalRatings} ratings)`, value: avgRating },
    { label: 'Followers', value: followerCount, key: 'followers', clickable: true },
    { label: 'Following', value: followingCount, key: 'following', clickable: true }
  ];

  return (
    <div className="min-h-screen profile-page">
      <div className="profile-hero card-glass">
        <div
          className="profile-hero-banner"
          style={{
            backgroundImage: profile?.banner_url ? `url(${profile.banner_url})` : undefined
          }}
        />
        <div className="profile-hero-overlay" />
        <div className="profile-hero-content">
          <div className="profile-hero-left">
            <div className="profile-avatar-stack">
              <div className="profile-avatar-xl">
                {editMode ? (
                  <>
                    {formState.avatar_url ? (
                      <img src={formState.avatar_url} alt={displayName} />
                    ) : (
                      <span>{displayName.slice(0, 2).toUpperCase()}</span>
                    )}
                  </>
                ) : (
                  <>
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt={displayName} />
                    ) : (
                      <span>{displayName.slice(0, 2).toUpperCase()}</span>
                    )}
                  </>
                )}
              </div>
              {editMode && (
                <>
                  <ImageUploadButton
                    label={uploadingMedia.avatar ? 'Uploading...' : 'Upload avatar'}
                    variant="button"
                    className="profile-upload-btn"
                    disabled={uploadingMedia.avatar}
                    onSelect={(file) => handleFilePick(file, 'avatar_url')}
                  />
                  {formState.avatar_url && (
                    <button
                      type="button"
                      className="btn-secondary btn-compact profile-upload-btn"
                      disabled={uploadingMedia.avatar}
                      onClick={() => handleDeleteMedia('avatar')}
                    >
                      Remove avatar
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="profile-hero-text">
              {editMode ? (
                <>
                  <input
                    className="input-glass"
                    value={formState.name}
                    onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Name"
                  />
                  <input
                    className="input-glass"
                    value={formState.username}
                    onChange={(e) => setFormState((prev) => ({ ...prev, username: e.target.value }))}
                    placeholder="Username"
                  />
                  <textarea
                    className="input-glass"
                    rows="3"
                    value={formState.bio}
                    onChange={(e) => setFormState((prev) => ({ ...prev, bio: e.target.value }))}
                    placeholder="Write a short bio"
                  />
                </>
              ) : (
                <>
                  <h1 className="text-2xl font-bold">{displayName}</h1>
                  {handle && <div className="profile-handle">{handle}</div>}
                  <p className="text-gray-400">
                    {profile?.bio || (isOwner ? 'No bio yet. Tell the community about your prompts.' : 'No bio yet.')}
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="profile-hero-actions">
            {isOwner ? (
              !editMode ? (
                <button className="btn-primary" onClick={() => setEditMode(true)}>
                  <Pencil className="w-4 h-4" /> Edit Profile
                </button>
              ) : (
                <button className="btn-secondary" onClick={() => setEditMode(false)}>
                  Cancel
                </button>
              )
            ) : (
              <button
                className={followStatus.following ? 'btn-secondary' : 'btn-primary'}
                onClick={handleFollowToggle}
                disabled={followLoading}
              >
                {followStatus.following ? <UserMinus className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                {followStatus.following ? 'Following' : 'Follow'}
              </button>
            )}
            <div className="profile-hero-icons">
              <button className="icon-btn" onClick={handleCopyProfile}>
                <Link2 className="w-4 h-4" />
              </button>
              <button className="icon-btn" onClick={handleCopyProfile}>
                <Share2 className="w-4 h-4" />
              </button>
              {!isOwner && (
                <button
                  className="icon-btn"
                  onClick={handleNotifyToggle}
                  disabled={!followStatus.following || followLoading}
                  data-tooltip={followStatus.notify ? 'Notifications on' : 'Notifications off'}
                  aria-label={followStatus.notify ? 'Notifications on' : 'Notifications off'}
                >
                  {followStatus.notify ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                </button>
              )}
              {copied && <span className="text-xs text-gray-400">Link copied</span>}
            </div>
            {followError && <div className="text-xs text-red-400">{followError}</div>}
          </div>
        </div>

        {editMode && (
          <div className="profile-edit-grid">
            <div>
              <label className="block text-sm font-medium mb-2">Profile Banner</label>
              <ImageUploadButton
                label={uploadingMedia.banner ? 'Uploading...' : 'Upload banner'}
                variant="button"
                disabled={uploadingMedia.banner}
                onSelect={(file) => handleFilePick(file, 'banner_url')}
              />
              {formState.banner_url && (
                <button
                  type="button"
                  className="btn-secondary btn-compact mt-3"
                  disabled={uploadingMedia.banner}
                  onClick={() => handleDeleteMedia('banner')}
                >
                  Remove banner
                </button>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Links (up to 3)</label>
              {formState.links.map((link, index) => (
                <input
                  key={`link-${index}`}
                  className="input-glass mb-2"
                  value={link}
                  onChange={(e) => {
                    const next = [...formState.links];
                    next[index] = e.target.value;
                    setFormState((prev) => ({ ...prev, links: next }));
                  }}
                  placeholder={`https://your-link-${index + 1}.com`}
                />
              ))}
            </div>
            <div className="profile-actions">
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
        )}

        {!editMode && linkList.length > 0 && (
          <div className="profile-links">
            {linkList.map((link) => {
              const href = normalizeLink(link);
              return (
                <a key={link} href={href} target="_blank" rel="noreferrer" className="link-pill">
                  <ExternalLink className="w-4 h-4" />
                  {link.replace(/^https?:\/\//i, '')}
                </a>
              );
            })}
          </div>
        )}
      </div>

      <div className="profile-stats">
        {stats.map((stat) => (
          <button
            key={stat.label}
            type="button"
            className={`stat-pill card-glass ${stat.clickable ? 'stat-clickable' : ''}`}
            onClick={() => {
              if (!stat.clickable) return;
              setFollowModal(stat.key);
              setFollowSearch('');
              setFollowVisibleCount(12);
            }}
          >
            <div className="stat-label">{stat.label}</div>
            <div className="stat-value">{stat.value}</div>
          </button>
        ))}
      </div>

      <div className="profile-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`option-pill ${activeTab === tab.key ? 'selected' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {(activeTab === 'overview' || activeTab === 'projects') && (
        <section className="profile-section">
          <div className="section-header">
            <div>
              <h2 className="text-xl font-bold">
                {isOwner ? 'Your Projects' : 'Public Projects'}
              </h2>
              <div className="text-xs text-gray-500">{projectFiltered.length} total</div>
            </div>
            <div className="page-header-actions">
              <button
                type="button"
                className="btn-secondary btn-compact"
                onClick={() => {
                  setSelectedProjectId(null);
                  setSelectedFolderId(null);
                }}
              >
                Show All
              </button>
              <div className="search-field">
                <Search className="w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search projects"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                />
              </div>
              <div className="sort-field">
                <div className="sort-label">Sort</div>
                <SelectMenu
                  value={projectSort}
                  options={[
                    { value: 'updated_desc', label: 'Recently Updated' },
                    { value: 'updated_asc', label: 'Least Recently Updated' },
                    { value: 'created_desc', label: 'Date Created (Newest)' },
                    { value: 'created_asc', label: 'Date Created (Oldest)' },
                    { value: 'name_asc', label: 'Alphabetical (A-Z)' },
                    { value: 'name_desc', label: 'Alphabetical (Z-A)' }
                  ]}
                  placeholder="Sort projects"
                  onChange={setProjectSort}
                />
              </div>
            </div>
          </div>
          {projectFiltered.length === 0 ? (
            <div className="card-glass empty-state">
              <h3 className="text-lg font-semibold">No projects yet</h3>
              <p className="text-gray-400">Create a project to organize prompts.</p>
            </div>
          ) : (
            <div className="tile-grid">
              {projectFiltered.map((project) => (
                <button
                  key={project.project_id}
                  type="button"
                  className={`tile-card ${selectedProjectId === project.project_id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedProjectId(project.project_id);
                    setSelectedFolderId(null);
                  }}
                >
                  <div className="tile-card-header">
                    <div className="tile-icon" style={{ color: project.color || '#3B82F6' }}>
                      <FolderOpen className="w-5 h-5" />
                    </div>
                    <div className="tile-card-actions">
                      <VisibilityIndicator visibility={project.visibility || 'private'} />
                      <span className="badge">{project.visibility || 'private'}</span>
                    </div>
                  </div>
                  <h3 className="tile-title">{project.name}</h3>
                  <p className="tile-sub">{project.description || 'No description yet.'}</p>
                  <div className="tile-meta">Updated {new Date(project.updated_at).toLocaleDateString()}</div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {selectedProjectId && (activeTab === 'overview' || activeTab === 'projects') && (
        <section className="profile-section">
          <div className="section-header">
            <div>
              <h2 className="text-xl font-bold">{isOwner ? 'Your Folders' : 'Public Folders'}</h2>
              <div className="text-xs text-gray-500">{folders.length} total</div>
            </div>
          </div>
          {folders.length === 0 ? (
            <div className="card-glass empty-state">
              <p className="text-gray-400">No folders in this project yet.</p>
            </div>
          ) : (
            <div className="tile-grid">
              {folders.map((folder) => (
                <button
                  key={folder.folder_id}
                  type="button"
                  className={`tile-card ${selectedFolderId === folder.folder_id ? 'selected' : ''}`}
                  onClick={() => setSelectedFolderId(folder.folder_id)}
                >
                  <div className="tile-card-header">
                    <div className="tile-icon" style={{ color: folder.color || '#3B82F6' }}>
                      <FolderOpen className="w-5 h-5" />
                    </div>
                    <div className="tile-card-actions">
                      <VisibilityIndicator visibility={folder.visibility || 'private'} />
                      <span className="badge">{folder.visibility || 'private'}</span>
                    </div>
                  </div>
                  <h3 className="tile-title">{folder.name}</h3>
                  <p className="tile-sub">{folder.description || 'No description yet.'}</p>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {(activeTab === 'overview' || activeTab === 'prompts') && (
        <section className="profile-section">
          <div className="section-header">
            <div>
              <h2 className="text-xl font-bold">{isOwner ? 'Your Prompts' : 'Public Prompts'}</h2>
              <div className="text-xs text-gray-500">{promptFiltered.length} total</div>
            </div>
            <div className="page-header-actions">
              <button
                type="button"
                className="btn-secondary btn-compact"
                onClick={() => {
                  setSelectedProjectId(null);
                  setSelectedFolderId(null);
                }}
              >
                Show All
              </button>
              <div className="search-field">
                <Search className="w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search prompts"
                  value={promptSearch}
                  onChange={(e) => setPromptSearch(e.target.value)}
                />
              </div>
              <div className="sort-field">
                <div className="sort-label">Sort</div>
                <SelectMenu
                  value={promptSort}
                  options={[
                    { value: 'updated_desc', label: 'Recently Updated' },
                    { value: 'updated_asc', label: 'Least Recently Updated' },
                    { value: 'created_desc', label: 'Date Created (Newest)' },
                    { value: 'created_asc', label: 'Date Created (Oldest)' },
                    { value: 'name_asc', label: 'Alphabetical (A-Z)' },
                    { value: 'name_desc', label: 'Alphabetical (Z-A)' },
                    { value: 'rating_desc', label: 'Top Rated' }
                  ]}
                  placeholder="Sort prompts"
                  onChange={setPromptSort}
                />
              </div>
            </div>
          </div>
          {promptFiltered.length === 0 ? (
            <div className="card-glass empty-state">
              <h3 className="text-lg font-semibold">No prompts yet</h3>
              <p className="text-gray-400">Create a prompt to showcase your work.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {promptFiltered
                .filter((prompt) => {
                  if (selectedFolderId) return prompt.folder_id === selectedFolderId;
                  if (selectedProjectId) return prompt.project_id === selectedProjectId;
                  return true;
                })
                .map((prompt) => {
                  const profileLabel = isOwner
                    ? 'Your Profile'
                    : (profile?.name || profile?.username || 'Profile');
                  const profilePath = isOwner ? '/profile' : buildProfilePath(profile);
                  return (
                  <Link
                    key={prompt.prompt_id}
                    className="card-glass cursor-pointer"
                    to={`/prompts/${prompt.prompt_id}`}
                    state={{
                      backTo: profilePath,
                      breadcrumbs: [{ label: profileLabel, to: profilePath }]
                    }}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-bold text-lg">{prompt.name}</h3>
                      <div className="flex items-center gap-2">
                        <VisibilityIndicator visibility={prompt.visibility || 'public'} />
                        <span className="badge">{prompt.visibility || 'public'}</span>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                      {richTextToPlainText(prompt.description) || 'No description'}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4" />
                          {Number(prompt.average_rating ?? 0).toFixed(1)}
                        </span>
                        <span>{new Date(prompt.updated_at).toLocaleDateString()}</span>
                      </div>
                      {prompt.categories?.name && <span className="badge">{prompt.categories.name}</span>}
                    </div>
                  </Link>
                );
                })}
            </div>
          )}
        </section>
      )}

      <Modal
        open={Boolean(followModal)}
        title={followModal === 'followers' ? 'Followers' : 'Following'}
        onClose={() => setFollowModal(null)}
        footer={(
          <div className="modal-actions">
            <button className="btn-secondary" type="button" onClick={() => setFollowModal(null)}>
              Close
            </button>
          </div>
        )}
      >
        {(() => {
          const dataset = followModal === 'followers' ? followersData : followingData;
          const isPrivate = dataset.private && !isOwner;
          const items = dataset.items || [];
          const term = followSearch.trim().toLowerCase();
          const filtered = term
            ? items.filter((user) => {
                const name = (user.name || '').toLowerCase();
                const handle = (user.username || '').toLowerCase();
                return name.includes(term) || handle.includes(term);
              })
            : items;
          const visibleItems = filtered.slice(0, followVisibleCount);
          return (
            <div className="follow-modal">
              <div className="search-field">
                <Search className="w-4 h-4" />
                <input
                  type="text"
                  placeholder={`Search ${followModal || ''}`}
                  value={followSearch}
                  onChange={(event) => {
                    setFollowSearch(event.target.value);
                    setFollowVisibleCount(12);
                  }}
                  disabled={isPrivate}
                />
              </div>
              {isPrivate ? (
                <div className="card-glass empty-state">
                  <p className="text-gray-400">This list is private.</p>
                </div>
              ) : (
                <>
                  <div className="follow-list">
                    {visibleItems.length === 0 ? (
                      <div className="card-glass empty-state">
                        <p className="text-gray-400">No users found.</p>
                      </div>
                    ) : (
                      visibleItems.map((user) => (
                        <Link key={user.user_id} to={buildProfilePath(user)} className="follow-row">
                          <div className="follow-avatar">
                            {user.avatar_url ? <img src={user.avatar_url} alt={user.name || user.username} /> : null}
                            {!user.avatar_url && <span>{(user.name || user.username || '?').slice(0, 2).toUpperCase()}</span>}
                          </div>
                          <div>
                            <div className="follow-name">{user.name || 'Rebel User'}</div>
                            <div className="follow-handle">{user.username ? `@${user.username}` : ''}</div>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                  {visibleItems.length < filtered.length && (
                    <button
                      type="button"
                      className="btn-secondary btn-compact"
                      onClick={() => setFollowVisibleCount((prev) => prev + 12)}
                    >
                      Load more
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })()}
      </Modal>

      {activeTab === 'favorites' && isOwner && (
        <section className="profile-section">
          <div className="card-glass empty-state">
            <h3 className="text-lg font-semibold">Manage Favorites</h3>
            <p className="text-gray-400">Open your full favorites workspace.</p>
            <Link to="/favorites" className="btn-secondary mt-4">
              Open Favorite Prompts
            </Link>
          </div>
        </section>
      )}

      {activeTab === 'saved' && isOwner && (
        <section className="profile-section">
          <div className="card-glass empty-state">
            <h3 className="text-lg font-semibold">Manage Saved Prompts</h3>
            <p className="text-gray-400">Open your saved outputs and reusable runs.</p>
            <Link to="/saved" className="btn-secondary mt-4">
              Open Saved Prompts
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}

export default ProfilePage;
